import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Alert
} from '@mui/material';
import { Add, ExpandMore, ExpandLess, AutoAwesome, Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { Seed } from '../../types/shared';

interface SeedSuggestionsProps {
    mode?: 'dialog' | 'inline';
    open?: boolean;
    onClose?: () => void;
    onAddSeed: (seed: Partial<Seed>) => void;
}

// Extended seed interface for suggestions since they might have extra fields like reason
interface SuggestedSeed extends Omit<Seed, 'id' | 'userId'> {
    reason?: string;
    estimatedShelfLifeMonths?: number;
}

const SeedSuggestions: React.FC<SeedSuggestionsProps> = ({ mode = 'dialog', open, onClose, onAddSeed }) => {
    const { t, i18n } = useTranslation();
    const [suggestions, setSuggestions] = useState<SuggestedSeed[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (mode === 'inline' || open) {
            fetchSuggestions();
        }
    }, [open, mode]);

    const fetchSuggestions = async () => {
        setLoading(true);
        setError(null);
        try {
            const suggestSeeds = httpsCallable<{ language: string }, { suggestions: SuggestedSeed[] }>(functions, 'suggestSeeds');
            const result = await suggestSeeds({ language: i18n.language });
            if (result.data && result.data.suggestions) {
                setSuggestions(result.data.suggestions);
            } else {
                throw new Error('No suggestions returned');
            }
        } catch (err) {
            console.error('Error fetching suggestions:', err);
            setError('Failed to load suggestions. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = (seed: SuggestedSeed) => {
        // Prepare seed data for adding
        const newSeed: Partial<Seed> & { reason?: string; estimatedShelfLifeMonths?: number } = {
            ...seed,
            // Ensure fields match what GardenOverview expects
            rawInput: { text: '', imageUrl: null, storagePath: null },
            processed: true // It comes from AI, so it is processed
        };

        // Calculate bestBefore if estimatedShelfLifeMonths is present
        if (seed.estimatedShelfLifeMonths) {
            const bestBefore = new Date();
            bestBefore.setMonth(bestBefore.getMonth() + seed.estimatedShelfLifeMonths);
            newSeed.bestBefore = bestBefore;
        }

        // Remove 'reason' as it's not part of the seed model
        delete newSeed.reason;
        delete newSeed.estimatedShelfLifeMonths;

        onAddSeed(newSeed as Partial<Seed>);
    };

    const content = (
        <>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }} color="text.secondary">
                        {t('seeds.suggestions_analyzing')}
                    </Typography>
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <List disablePadding>
                    {suggestions.map((seed, index) => (
                        <SuggestionItem key={index} seed={seed} onAdd={() => handleAdd(seed)} />
                    ))}
                    {suggestions.length === 0 && !loading && (
                        <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
                            {t('seeds.suggestions_none')}
                        </Typography>
                    )}
                </List>
            )}
        </>
    );

    if (mode === 'inline') {
        return content;
    }

    return (
        <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome color="secondary" />
                {t('seeds.suggestions_title')}
            </DialogTitle>
            <DialogContent dividers>
                {content}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('seeds.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

interface SuggestionItemProps {
    seed: SuggestedSeed;
    onAdd: () => void;
}

const SuggestionItem: React.FC<SuggestionItemProps> = ({ seed, onAdd }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [added, setAdded] = useState(false);

    const handleAddClick = () => {
        onAdd();
        setAdded(true);
    };

    return (
        <ListItem
            alignItems="flex-start"
            secondaryAction={
                <Button
                    variant={added ? "contained" : "outlined"}
                    color={added ? "success" : "primary"}
                    startIcon={added ? <Check /> : <Add />}
                    size="small"
                    onClick={handleAddClick}
                    disabled={added}
                >
                    {added ? t('seeds.added') : t('common.add')}
                </Button>
            }
            sx={{
                bgcolor: 'background.paper',
                mb: 1,
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.05)',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1, pr: 8 }}>
                <ListItemText
                    primary={
                        <Typography variant="subtitle1" component="h3" className="serif-text" fontWeight="bold">
                            {seed.name}
                        </Typography>
                    }
                    secondary={seed.reason} // Display the reasoning here
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', pr: 8 }}>
                <Chip
                    label={t('seeds.view_details')}
                    size="small"
                    variant="outlined"
                    onClick={() => setExpanded(!expanded)}
                    icon={expanded ? <ExpandLess /> : <ExpandMore />}
                    sx={{ height: 24 }}
                />
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%', mt: 1 }}>
                <Box sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {seed.notes}
                    </Typography>
                </Box>
            </Collapse>
        </ListItem>
    );
};

export default SeedSuggestions;
